import React, { useEffect, useMemo, useState } from "react";
import { Eye } from "lucide-react";
import StatementPreviewDialog, {
  type PreviewDocument,
} from "@/components/application/StatementPreviewDialog";
import { Button } from "@/components/ui/button";
import { docusealService, getDocusealErrorMessage } from "@/services/docuseal";

type PdfFile = { blob: Blob; filename: string };

/**
 * Opens a contract document's blank template PDF in the in-app viewer, or with `load`,
 * any contract PDF (e.g. a signed copy).
 */
const TemplatePreviewButton: React.FC<
  ({ documentId: number; load?: never } | { documentId?: never; load: () => Promise<PdfFile> }) & {
    disabled?: boolean;
    label?: string;
    /** The accessible name when the visible label is shortened. */
    ariaLabel?: string;
    title?: string;
    /** Lets a caller match the button to neighbouring field heights. */
    className?: string;
    /** Called with null when a preview starts and with the message when it fails. */
    onError: (message: string | null) => void;
  }
> = ({ documentId, load, disabled, label = "View", ariaLabel, title, className, onError }) => {
  const [preview, setPreview] = useState<PreviewDocument | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const documents = useMemo(() => (preview ? [preview] : []), [preview]);
  const fileUrl = preview?.file_url;
  // Frees each blob once it is replaced, closed, or the button unmounts.
  useEffect(
    () => () => {
      if (fileUrl) window.URL.revokeObjectURL(fileUrl);
    },
    [fileUrl]
  );

  const openPreview = async () => {
    setPreviewing(true);
    onError(null);
    try {
      const { blob, filename } = load
        ? await load()
        : await docusealService.downloadTemplateDocument(documentId as number);
      setPreview({
        key: String(documentId ?? filename),
        filename,
        file_url: window.URL.createObjectURL(blob),
      });
    } catch (err) {
      onError(getDocusealErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={className}
        disabled={disabled || previewing}
        title={title}
        aria-label={ariaLabel}
        onClick={() => void openPreview()}
      >
        <Eye />
        {label}
      </Button>
      <StatementPreviewDialog
        documents={documents}
        activeKey={preview?.key ?? null}
        onClose={() => setPreview(null)}
      />
    </>
  );
};

export default TemplatePreviewButton;
